// Event documents — collaborative rich-text docs (speaker bios, venue info,
// FAQs, policies…) attendees read in the apps once published. Content lives in a
// Yjs room (EVENT_DOCUMENT_ROOM DO, see core/yjs-provider createDocumentProvider);
// publishing snapshots that room to R2. Mirrors core/event-pages.ts conventions.
import { api } from "./api"

export type DocumentRow = {
  id: string
  slug: string
  title: string
  kind: string
  yjsRoomId: string
  quickLink: number
  agendaItemId: string | null
  agendaActivityId: string | null
  agendaGroupId: string | null
  attendeeGroupId: string | null
  currentSnapshot: { version: number; publishedAt: string } | null
  modifiedAt?: string | null
  createdAt?: string | null
}

export type DocumentsPayload = { success: true; documents: DocumentRow[] }

export const KIND_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "generic", label: "Generic" },
  { value: "emergency_info", label: "Emergency information" },
  { value: "travel", label: "Travel info" },
  { value: "accommodation", label: "Accommodation info" },
  { value: "speaker_bio", label: "Speaker bio" },
  { value: "venue", label: "Venue info" },
  { value: "policy", label: "Policy" },
  { value: "faq", label: "FAQ" },
  { value: "sponsor", label: "Sponsor" },
  { value: "playbook", label: "Playbook" },
]
const KIND_LABEL = new Map(KIND_OPTIONS.map((k) => [k.value, k.label]))
export function kindLabel(kind: string): string {
  return KIND_LABEL.get(kind) ?? kind.replace(/[_-]/g, " ").replace(/^./, (c) => c.toUpperCase())
}

export function documentsKey(eventId: string) {
  return ["event-documents", eventId] as const
}

export function documentsQueryOptions(eventId: string) {
  return {
    queryKey: documentsKey(eventId),
    queryFn: () =>
      api<DocumentsPayload>("/api/v1/event-documents/list", {
        method: "POST",
        body: { eventId },
      }),
  }
}

export type CreateDocumentInput = {
  eventId: string
  slug: string
  title: string
  kind?: string
}

export function createEventDocument(input: CreateDocumentInput) {
  return api<{ success: true; document: DocumentRow }>("/api/v1/event-documents/create", {
    method: "POST",
    body: input,
  })
}

export type DocumentPatch = {
  title?: string
  kind?: string
  quickLink?: boolean
}

export function updateEventDocument(documentId: string, patch: DocumentPatch) {
  return api<{ success: true }>("/api/v1/event-documents/update", {
    method: "POST",
    body: { documentId, ...patch },
  })
}

export function deleteEventDocument(documentId: string) {
  return api<{ success: true; documentId: string }>("/api/v1/event-documents/delete", {
    method: "POST",
    body: { documentId },
  })
}

export type PublishDocumentResult = { success: true; version: number; htmlR2Key: string }

export function publishEventDocument(documentId: string) {
  return api<PublishDocumentResult>("/api/v1/event-documents/publish", {
    method: "POST",
    body: { documentId },
  })
}

// Slugify a title into a doc slug (lowercase, a–z0–9 + hyphens). Matches the
// backend's ^[a-z0-9-]+$ constraint. Spaces and other invalid characters are
// dropped (not turned into hyphens) so auto-derived slugs read as one word;
// hyphens the user types are preserved — including a trailing one, so "foo-"
// isn't stripped mid-typing.
export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "")
    .slice(0, 100)
}
